import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { 
  getAuth, 
  signOut, 
  signInWithEmailAndPassword, 
  onAuthStateChanged,
  type User 
} from 'firebase/auth';
import { BehaviorSubject } from 'rxjs';
import { 
  getFirestore, 
  doc, 
  getDoc 
} from 'firebase/firestore';

interface UserRoles {
  subscriber?: boolean;
  superadmin?: boolean;
  admin?: boolean;
  editor?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  currentUser$ = this.currentUserSubject.asObservable();
  
  private userRolesSubject = new BehaviorSubject<string[]>([]);
  userRoles$ = this.userRolesSubject.asObservable();

  constructor(private http: HttpClient) {
    const auth = getAuth();
    onAuthStateChanged(auth, async (user) => {
      this.currentUserSubject.next(user);
      if (user) {
        await this.fetchUserRoles(user.uid);
      } else {
        this.userRolesSubject.next([]);
      }
    });
  }

  private async fetchUserRoles(userId: string): Promise<void> {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      console.error('Attempting to fetch roles while not authenticated');
      this.userRolesSubject.next([]);
      return;
    }

    const db = getFirestore();
    const userDoc = doc(db, 'users', userId);
    
    try {
      const docSnap = await getDoc(userDoc);
      if (docSnap.exists()) {
        const userData = docSnap.data();
        const roles = userData['roles'] as UserRoles;
        const userRoles: string[] = [];
        
        if (roles?.superadmin) userRoles.push('superadmin');
        if (roles?.admin) userRoles.push('admin');
        if (roles?.editor) userRoles.push('editor');
        if (roles?.subscriber) userRoles.push('subscriber');
        
        this.userRolesSubject.next(userRoles);
      } else {
        console.warn(`No user document found for user ${userId}`);
        this.userRolesSubject.next([]);
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes('permission-denied')) {
        console.error('Permission denied accessing user document. Please check Firestore rules.');
      } else {
        console.error('Error fetching user roles:', error);
      }
      this.userRolesSubject.next([]);
      // Optionally force logout if we can't read roles
      if (error instanceof Error && error.message.includes('permission-denied')) {
        await this.logout();
      }
    }
  }

  async login(email: string, password: string): Promise<void> {
    const auth = getAuth();
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  isAuthenticated(): boolean {
    return this.currentUserSubject.value !== null;
  }

  hasAnyRole(roles: string[]): boolean {
    // Empty roles array means accessible to everyone
    if (!roles || roles.length === 0) return true;
    const currentRoles = this.userRolesSubject.value;
    // Superadmin has access to everything
    if (currentRoles.includes('superadmin')) return true;
    return roles.some(role => currentRoles.includes(role));
  }

  async logout(): Promise<void> {
    const auth = getAuth();
    try {
      await signOut(auth);
      this.currentUserSubject.next(null);
      this.userRolesSubject.next([]);
      window.location.href = '/login';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }
} 